/**
 * FAQ component - Frequently Asked Questions with accordion interface
 * Addresses common user questions and concerns
 */

import { useState } from 'react'
import { ChevronDown, HelpCircle } from 'lucide-react'
import '../faq.css'

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null)

  const toggleQuestion = (index) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  const faqs = [
    {
      category: 'Using the Calculator',
      questions: [
        {
          question: 'How accurate is this debt calculator?',
          answer: 'Our calculator uses the same amortization formulas employed by banks and financial institutions worldwide. The calculations are 100% accurate based on the inputs you provide. However, actual payoff amounts may vary slightly due to factors like payment timing, fees, late charges, or interest rate changes. Always verify final payoff amounts with your lender.'
        },
        {
          question: 'What information do I need to use the calculator?',
          answer: 'You need three pieces of information: (1) Your current debt balance (principal amount), (2) Your Annual Percentage Rate (APR), which you can find on your statement, and (3) The monthly payment amount you plan to make. Our calculator accepts values for credit cards, personal loans, auto loans, student loans, and other consumer debt.'
        },
        {
          question: 'Is my financial information stored or shared?',
          answer: 'Absolutely not. All calculations happen directly in your web browser using JavaScript. No data is transmitted to our servers, stored in databases, or shared with third parties. Your financial information remains completely private and secure on your device. You can even use the calculator offline once the page has loaded.'
        },
        {
          question: 'Can I use this for multiple debts at once?',
          answer: 'The calculator is designed for one debt at a time to provide detailed, accurate projections. However, you can run calculations separately for each debt and then use the results to plan a comprehensive debt payoff strategy (like the debt avalanche or snowball method). Some users create spreadsheets to track multiple calculations side by side.'
        }
      ]
    },
    {
      category: 'Understanding Results',
      questions: [
        {
          question: 'What does the payment schedule show?',
          answer: 'The payment schedule provides a month-by-month breakdown of your debt payoff journey. For each payment, you\'ll see: the payment number, the amount applied to interest, the amount applied to principal, and your remaining balance. This helps you understand how your payments are allocated and track your progress toward becoming debt-free.'
        },
        {
          question: 'Why does so much of my payment go to interest at first?',
          answer: 'This is how amortization works. Early in repayment, your balance is highest, so interest charges are higher. Each month, interest is calculated as: (remaining balance × APR ÷ 12). Your payment first covers that interest, with the remainder reducing principal. As your balance decreases, less goes to interest and more to principal, creating an accelerating payoff effect.'
        },
        {
          question: 'How can I reduce my payoff time?',
          answer: 'There are several strategies: (1) Increase your monthly payment—even small increases make a big difference, (2) Make bi-weekly payments instead of monthly (results in 13 payments per year), (3) Apply windfalls (tax refunds, bonuses) directly to principal, (4) Round up payments to the nearest $50 or $100, or (5) Consider refinancing high-interest debt to a lower rate.'
        },
        {
          question: 'What if I can only afford the minimum payment?',
          answer: 'Our calculator will show you exactly how long minimum payments will take and how much interest you\'ll pay. This transparency often motivates people to find ways to pay more. If you truly can\'t pay more, consider: (1) Debt consolidation at a lower rate, (2) Contacting your lender about hardship programs, (3) Meeting with a non-profit credit counselor (NFCC.org), or (4) Temporarily increasing income through a side job.'
        }
      ]
    },
    {
      category: 'Debt Payoff Strategies',
      questions: [
        {
          question: 'What\'s the difference between debt avalanche and debt snowball?',
          answer: 'Debt avalanche prioritizes paying off debts with the highest interest rates first, saving the most money in total interest. Debt snowball focuses on smallest balances first, providing psychological wins and motivation. Avalanche is mathematically optimal, but snowball has higher success rates for some people due to the motivational boost. Choose the method that best fits your personality.'
        },
        {
          question: 'Should I pay off debt or save for emergencies first?',
          answer: 'Financial experts recommend having a small emergency fund ($500-$1,000) before aggressively tackling debt. This prevents you from going further into debt when unexpected expenses arise. Once you have this cushion, focus on high-interest debt (>7% APR) while maintaining minimum payments on everything else. After becoming debt-free, build a 3-6 month emergency fund.'
        },
        {
          question: 'Is debt consolidation a good idea?',
          answer: 'Consolidation can be excellent if it lowers your interest rate and you don\'t extend the repayment term significantly. For example, consolidating 18% credit card debt to a 7% personal loan saves substantial interest. However, beware of: (1) Extending the term so much that you pay more total interest despite a lower rate, (2) Fees that negate savings, and (3) Accumulating new debt on paid-off credit cards.'
        },
        {
          question: 'Should I pay off debt or invest for retirement?',
          answer: 'Generally, pay off high-interest debt (>7-8%) before investing, as you\'re "earning" a guaranteed return equal to that interest rate. For low-interest debt (<4-5%), consider investing instead, especially in tax-advantaged retirement accounts with employer matching—that\'s free money. For moderate debt (4-7%), many experts suggest doing both: contribute enough to get the full employer match, then attack debt aggressively.'
        }
      ]
    },
    {
      category: 'Technical & Account Questions',
      questions: [
        {
          question: 'Why doesn\'t the calculator accept my payment amount?',
          answer: 'Your monthly payment must be higher than the monthly interest charge, or your debt will never decrease. Monthly interest is calculated as: (balance × APR ÷ 12). For example, on a $10,000 balance at 18% APR, monthly interest is $150. Your payment must exceed $150 to make progress. If you can\'t afford more than the interest, contact your lender immediately about hardship options.'
        },
        {
          question: 'Can I print or save my payment schedule?',
          answer: 'Yes! You can use your browser\'s print function (Ctrl+P or Cmd+P) to save the payment schedule as a PDF. You can also screenshot the results or copy the data to a spreadsheet for tracking. Many users print their schedule and mark off each payment as they progress, finding it motivating to visualize their journey to debt freedom.'
        },
        {
          question: 'Does this calculator work for mortgages?',
          answer: 'While the underlying math is the same, this calculator is optimized for consumer debt with simpler structures. Mortgages involve additional complexities like property taxes, insurance (escrow), PMI, and potential prepayment penalties. For mortgages, we recommend using a dedicated mortgage calculator that accounts for these specific factors. However, you can use this for rough estimates of principal and interest only.'
        },
        {
          question: 'Do I need to create an account to use the calculator?',
          answer: 'No! Our calculator is completely free and requires no registration, login, or personal information. Simply enter your debt details and get instant results. This design ensures maximum privacy—your data never leaves your device. You can use the calculator as many times as you want, with as many different scenarios as you need, all without creating an account.'
        }
      ]
    }
  ]

  return (
    <section id="faq" className="faq-section">
      <div className="section-container">
        <div className="section-header">
          <HelpCircle size={48} className="header-icon" />
          <h2>Frequently Asked Questions</h2>
          <p className="section-subtitle">
            Everything you need to know about using our debt calculator and managing your debt payoff journey
          </p>
        </div>

        <div className="faq-container">
          {faqs.map((category, categoryIndex) => (
            <div key={categoryIndex} className="faq-category">
              <h3 className="category-title">{category.category}</h3>
              <div className="questions-list">
                {category.questions.map((faq, questionIndex) => {
                  const globalIndex = `${categoryIndex}-${questionIndex}`
                  const isOpen = openIndex === globalIndex

                  return (
                    <div
                      key={questionIndex}
                      className={`faq-item ${isOpen ? 'open' : ''}`}
                    >
                      <button
                        className="faq-question"
                        onClick={() => toggleQuestion(globalIndex)}
                        aria-expanded={isOpen}
                      >
                        <span>{faq.question}</span>
                        <ChevronDown
                          size={20}
                          className={`chevron ${isOpen ? 'rotated' : ''}`}
                        />
                      </button>
                      <div className={`faq-answer ${isOpen ? 'expanded' : ''}`}>
                        <div className="answer-content">
                          {faq.answer}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Still Have Questions CTA */}
        <div className="faq-cta">
          <h3>Still Have Questions?</h3>
          <p>
            Can't find the answer you're looking for? While we don't offer direct support,
            we recommend consulting with a certified financial advisor or credit counselor
            for personalized guidance on your specific situation.
          </p>
          <div className="resources-links">
            <a href="https://www.nfcc.org" target="_blank" rel="noopener noreferrer" className="resource-link">
              Find a Credit Counselor (NFCC)
            </a>
            <a href="https://www.napfa.org" target="_blank" rel="noopener noreferrer" className="resource-link">
              Find a Financial Planner (NAPFA)
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

export default FAQ
